import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useFormik } from "formik";
import { capitalizeFirstLetter, globalLoader, handleServerValidations, TrimText } from "../../../../utils/commonfunction";
import { categoriesList, registerBusiness, subcategoriesList } from "../../services/auth.services";
import { OFFER_PERCENTAGE, SWAL_SETTINGS } from "../../../../utils/Constants";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import backLogo from "../../../../assets/business/images/Button-icon.svg";
import logo from "../../../../assets/Discount_App_logo.png";
import * as Yup from "yup";
import BusinessPhoneInput from "../../../../utils/businessPhoneInput";
import LocationPicker from "../../../../utils/map.js";

const BusinessRegister = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [previewImage, setPreviewImage] = useState("");
  const [brochureImage, setBrochureImage] = useState("");
  const [categoryData, setCategoryData] = useState([]);
  const [subcategoryData, setSubCategoryData] = useState([]);
  const [profileImageError, setProfileImageError] = useState("");
  const [brochureError, setBrochureError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);



  const SUPPORTED_IMAGE_FORMATS = ["image/jpeg", "image/png", "image/webp"];
  const SUPPORTED_BROCHURE_FORMATS = ["image/jpeg", "image/png", "image/webp"];

  const preventSpace = (e) => {
    // Prevent spacebar (keyCode 32) from being pressed
    if (e.keyCode === 32 || e.key === " ") {
      e.preventDefault();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validationSchema = Yup.object().shape({
    business_name: Yup.string()
      .required("Business name is required")
      .trim()
      .matches(/^[a-zA-Z0-9\s&'-]+$/, "Invalid business name"),
    email: Yup.string()
      .required("Email is required")
      .matches(/^(?!.*\.\.)(?!\.)(?!.*\.$)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email"),
    phone_number: Yup.object().shape({
      countryCode: Yup.string().required("Please select a country code"),
      phone: Yup.string()
        .matches(/^[0-9]+$/, "Phone number must contain digits only")
        .min(6, "Phone number must be at least 6 digits")
        .max(15, "Phone number must not exceed 15 digits")
        .required("Phone number is required"),
      fullNumber: Yup.string().required("Full phone number is missing"),
    }),
    address: Yup.object().shape({
      lat: Yup.number().required("Latitude is required"),
      lng: Yup.number().required("Longitude is required"),
      address: Yup.string()
        .required("Full address is required")
        .trim()
        .matches(/^[a-zA-Z0-9\s&',.\-/()#]+$/, "Invalid business address")
    })
    .required("Address is required"),
    
    vat_number: Yup.string()
      .trim()
      .test("vat-length", "VAT number must be exactly 15 characters", (value) => !value || value.length === 15)
      .matches(/^[a-zA-Z0-9\s&'-]+$/, { message: "Invalid VAT number", excludeEmptyString: true }),
    brochure_title: Yup.string()
      .required("Business title is required")
      .matches(/^[a-zA-Z0-9\s&'-]+$/, "Invalid brochure title"),
    password: Yup.string().trim().min(6, "Minimum 6 characters").required("Password is required"),
    confirm_password: Yup.string()
      .trim()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm password is required"),
    offer_percentage: Yup.string().required("Offer percentage is required"),
    category_id: Yup.string().required("Category is required"),
    subcategory_id: Yup.string().required("Sub category is required"),
    isTermsConditionAccept: Yup.boolean().oneOf([true], "You must accept Terms & Conditions and Privacy Policy"),
    profile_image: Yup.mixed().test("fileFormat", "Unsupported file format. Only JPG, PNG, or WEBP allowed.", (value) => !value || (value && SUPPORTED_IMAGE_FORMATS.includes(value.type))),
    brochure: Yup.mixed().test("fileFormat", "Unsupported file format. Only JPG, PNG or WEBP allowed.", (value) => !value || (value && SUPPORTED_BROCHURE_FORMATS.includes(value.type))),
  });

  const formik = useFormik({
    initialValues: {
      business_name: "",
      business_type: "",
      email: "",
      phone_number: {
        countryCode: "",
        phone: "",
        fullNumber: "",
      },
      address: "",
      vat_number: "",
      password: "",
      brochure_title: "",
      confirm_password: "",
      offer_percentage: "",
      profile_image: "",
      brochure: "",
      isTermsConditionAccept: false,
      category_id: "",
      subcategory_id: "",
    },
    validateOnBlur: false,
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      globalLoader(true);
      const formData = new FormData();
      formData.append("business_name", values.business_name);
      formData.append("business_type", values.business_type);
      formData.append("email", values.email.toLowerCase());
      formData.append("countryCode", values.phone_number.countryCode);
      formData.append("phone", values.phone_number.phone);
      formData.append("fullNumber", values.phone_number.fullNumber);
      formData.append("address", values.address.address);
      formData.append("vat_number", values.vat_number);
      formData.append("brochure_title", values.brochure_title);
      formData.append("password", values.password);
      formData.append("confirm_password", values.confirm_password);
      formData.append("isTermsConditionAccept", values.isTermsConditionAccept);
      formData.append("offer_percentage", values.offer_percentage);
      formData.append("category_id", values.category_id);
      formData.append("subcategory_id", values.subcategory_id);
      formData.append("latitude", values.address.lat);
      formData.append("longitude", values.address.lng);

      formData.append("city", values.address.city);
      formData.append("state", values.address.state);
      formData.append("postalCode", values.address.postalCode);
      formData.append("country", values.address.country);

      if (values.profile_image) {
        formData.append("profile_image", values.profile_image);
      }
      if (values.brochure) {
        formData.append("brochure", values.brochure);
      }
      registerBusiness(formData)
        .then((response) => {
          globalLoader(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            resetForm();
            navigate("/business/register-otp-verification", {
              state: {
                email: values.email,
                phone_number: values.phone_number,
              },
            });
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          globalLoader(false);
          console.log("error--->", error);
        });
    },
  });

  useEffect(() => {
    categoriesList()
      .then((response) => {
        if (response?.success) {
          setCategoryData(response?.data?.list || []);
        }
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, []);

  useEffect(() => {
    if (formik?.values?.category_id) {
      subcategoriesList({ category: formik.values.category_id })
        .then((response) => {
          if (response?.success) {
            setSubCategoryData(response?.data?.list || []);
          }
        })
        .catch((error) => {
          console.log("error", error);
        });
    } else {
      setSubCategoryData([]);
    }
  }, [formik?.values?.category_id]);

  const handleProfileImageChange = (event) => {
    const file = event.currentTarget.files[0];
    setProfileImageError(""); // Clear previous error

    if (file) {
      // Check for allowed image types
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        setProfileImageError("Only JPG, PNG and WEBP files are allowed.");
        formik.setFieldValue("profile_image", "");
        setPreviewImage("");
        return;
      }

      formik.setFieldValue("profile_image", file);
      setPreviewImage(file.name); // Store filename instead of URL
    } else {
      formik.setFieldValue("profile_image", "");
      setPreviewImage("");
    }
  };

  const handleBrochureChange = (event) => {
    const file = event.currentTarget.files[0];
    setBrochureError(""); // Clear previous error

    if (file) {
      // Check for allowed image types
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        setBrochureError("Only JPG, PNG and WEBP files are allowed.");
        formik.setFieldValue("brochure", "");
        setBrochureImage("");
        return;
      }

      formik.setFieldValue("brochure", file);
      setBrochureImage(file.name); // Store filename instead of URL
    } else {
      formik.setFieldValue("brochure", "");
      setBrochureImage("");
    }
  };

  // Add these new functions for removing images:
  const removeProfileImage = () => {
    formik.setFieldValue("profile_image", "");
    setPreviewImage("");
    setProfileImageError("");
    // Clear the file input
    const fileInput = document.querySelector('input[name="profile_image"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const removeBrochureImage = () => {
    formik.setFieldValue("brochure", "");
    setBrochureImage("");
    setBrochureError("");

    // Clear the file input
    const fileInput = document.querySelector('input[name="brochure"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <div className="container section-section">
      <div className="text-start mt-3">
        <figure>
          <Link to={"/business/login"}>
            <img src={backLogo} alt="button" />
          </Link>
        </figure>
      </div>
      <div className="form-wrapper text-center">
        <div className="logo">
          <img src={logo} alt="Logo" />
        </div>
        <h4 className="h3-headline mb-5">Create Your Business Account</h4>
        <form onSubmit={formik.handleSubmit}>
          <div className="row g-3 text-start">
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="business_name">
                Business Name<span className="requirestar">*</span>
              </label>
              <input
                name="business_name"
                id="business_name"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.business_name}
                className="form-control"
                placeholder="Enter the name of your business"
              />

              <span className="text-danger d-flex text-left">
                {formik.touched.business_name && formik.errors.business_name ? <div className="text-danger d-flex text-left">{t(formik.errors.business_name)}</div> : null}
              </span>
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="category_id">
                Select Category<span className="requirestar">*</span>
              </label>
              <select className="form-control form-select cp" name="category_id" value={formik?.values?.category_id} onChange={formik.handleChange}>
                <option value={""} defaultChecked>
                  {"Select your business type"}
                </option>
                {categoryData.map((item, index) => {
                  return (
                    <option key={index} value={item._id}>
                      {capitalizeFirstLetter(item.title)}
                    </option>
                  );
                })}
              </select>
              {formik.touched.category_id && formik.errors.category_id ? <div className="text-danger d-flex text-left">{t(formik.errors.category_id)}</div> : null}
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="subcategory_id">
                Select Sub Category<span className="requirestar">*</span>
              </label>
              <select id="subcategory_id" className="form-control form-select cp" name="subcategory_id" value={formik?.values?.subcategory_id} onChange={formik.handleChange}>
                <option value={""}>{t("Select subcategory")}</option>
                {subcategoryData.map((item, index) => {
                  return (
                    <option key={index} value={item._id}>
                      {capitalizeFirstLetter(item.title)}
                    </option>
                  );
                })}
              </select>
              {formik.touched.subcategory_id && formik.errors.subcategory_id ? <div className="text-danger d-flex text-left">{t(formik.errors.subcategory_id)}</div> : null}
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="email">
                Email Address<span className="requirestar">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="email"
                name="email"
                placeholder="Enter email address"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <span className="text-danger">{formik.touched.email && formik.errors.email ? <div className="text-danger d-flex text-left">{t(formik.errors.email)}</div> : null}</span>
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="phone_number">
                Phone Number<span className="requirestar">*</span>
              </label>
              <BusinessPhoneInput
                name="phone_number"
                id="phone_number"
                onChange={(phoneObj) => {
                  formik.setFieldValue("phone_number", phoneObj);
                }}
                onBlur={formik.handleBlur}
                value={formik.values.phone_number}
                className="form-control"
                placeholder={t("Enter phone number")}
              />
              <span className="text-danger d-flex text-left">
                {formik?.touched?.phone_number?.phone && formik?.errors?.phone_number?.phone ? <div className="text-danger d-flex text-left">{t(formik.errors.phone_number.phone)}</div> : null}
              </span>
            </div>
            <div className="col-md-6 form-group position-relative">
              <label htmlFor="address" className="form-label subtitle-1">
                Business Address<span className="requirestar">*</span>
              </label>

              <input
                name="address"
                id="address"
                type="text"
                value={TrimText(formik.values.address.address, 25)}
                readOnly // 🔹 prevent manual typing
                className="form-control"
                placeholder={t("Enter business address")}
                data-bs-toggle="modal"
                data-bs-target="#locationModal" // 🔹 clicking opens the map modal
              />

              {/* Location Picker → will open on input click */}
              <LocationPicker
                onConfirm={(location) => {
                  console.log(location);
                  formik.setFieldValue("address", {
                    lat: location.lat,
                    lng: location.lng,
                    address: location.address,
                    city: location.city,
                    state: location.state,
                    postalCode: location.postalCode,
                    country: location.country,
                  });

               
                }}
              />

              <span className="text-danger d-flex text-left">
                {formik.touched.address && formik.errors.address ? <div className="text-danger d-flex text-left">{t(Object.values(formik.errors.address)[0])}</div> : null}

               
              </span>
            </div>

            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="vat_number">
                VAT Number
              </label>
              <input
                name="vat_number"
                id="vat_number"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.vat_number}
                className="form-control"
                placeholder={t("Enter VAT number")}
              />
              <span className="text-danger d-flex text-left">
                {formik.touched.vat_number && formik.errors.vat_number ? <div className="text-danger d-flex text-left">{t(formik.errors.vat_number)}</div> : null}
              </span>
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="brochure_title">
                Brochure Title<span className="requirestar">*</span>
              </label>
              <input
                name="brochure_title"
                id="brochure_title"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.brochure_title}
                className="form-control"
                placeholder={t("Enter brochure title")}
              />
              <span className="text-danger d-flex text-left">
                {formik.touched.brochure_title && formik.errors.brochure_title ? <div className="text-danger d-flex text-left">{t(formik.errors.brochure_title)}</div> : null}
              </span>
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="password">
                Password<span className="requirestar">*</span>
              </label>
              <div className="position-relative">
                <input
                  name="password"
                  id="password"
                  type={showPassword ? "text" : "password"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  className="form-control"
                  placeholder={t("Enter password")}
                  onKeyDown={preventSpace}
                  style={{ paddingRight: "45px" }}
                />

                <button
                  type="button"
                  className="btn btn-link position-absolute top-50 end-0 translate-middle-y pe-3"
                  onClick={togglePasswordVisibility}
                  style={{
                    border: "none",
                    background: "none",
                    padding: "0",
                    color: "#6c757d",
                    textDecoration: "none",
                  }}
                >
                  <i className={showPassword ? "fi-rr-eye-crossed" : "fi-rr-eye"}></i>
                </button>
              </div>
              <span className="text-danger d-flex text-left">
                {formik.touched.password && formik.errors.password ? <div className="text-danger d-flex text-left">{t(formik.errors.password)}</div> : null}
              </span>
            </div>
            <div className="col-md-6">
              <label className="form-label subtitle-1" htmlFor="confirm_password">
                Confirm Password<span className="requirestar">*</span>
              </label>
              <div className="position-relative">
                <input
                  name="confirm_password"
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.confirm_password}
                  className="form-control"
                  placeholder={t("Enter confirm password")}
                  onKeyDown={preventSpace}
                  style={{ paddingRight: "45px" }}
                />

                <button
                  type="button"
                  className="btn btn-link position-absolute top-50 end-0 translate-middle-y pe-3"
                  onClick={toggleConfirmPasswordVisibility}
                  style={{
                    border: "none",
                    background: "none",
                    padding: "0",
                    color: "#6c757d",
                    textDecoration: "none",
                  }}
                >
                  <i className={showConfirmPassword ? "fi-rr-eye-crossed" : "fi-rr-eye"}></i>
                </button>
              </div>
              <span className="text-danger d-flex text-left">
                {formik.touched.confirm_password && formik.errors.confirm_password ? <div className="text-danger d-flex text-left">{t(formik.errors.confirm_password)}</div> : null}
              </span>
            </div>
            <div className="col-12">
              <label className="form-label subtitle-1" htmlFor="offer_percentage">
                Select Offer Percentage<span className="requirestar">*</span>
              </label>
              <select className="form-control form-select cp" id="offer_percentage" name="offer_percentage" value={formik?.values?.offer_percentage} onChange={formik.handleChange}>
                <option value={""}>{t("Select")}</option>
                {OFFER_PERCENTAGE.map((item, index) => {
                  return (
                    <option key={index} value={item.value}>
                      {item.label}
                    </option>
                  );
                })}
              </select>
              {formik.touched.offer_percentage && formik.errors.offer_percentage ? <div className="text-danger d-flex text-left">{t(formik.errors.offer_percentage)}</div> : null}
            </div>
            <div className="col-12">
              <label className="form-label subtitle-1" htmlFor="profile_image">
                Business Photo
              </label>

              {!previewImage ? (
                <div className="custom-file-upload">
                  <div className="custom-upload-btn">
                    <span>Browse</span>
                    <i className="fi fi-br-upload" />
                  </div>
                  <input type="file" id="profile_image" name="profile_image" accept="image/jpeg, image/png, image/webp" onChange={handleProfileImageChange} />
                </div>
              ) : (
                <div className="mt-2 d-flex align-items-center justify-content-between p-2 bg-light rounded">
                  <span className="text-truncate me-2">
                    <i className="fi fi-rr-document me-1"></i>
                    {previewImage}
                  </span>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={removeProfileImage} title="Remove image">
                    <i className="fi fi-rr-cross-small" style={{ position: "relative", top: "2px" }}></i>
                  </button>
                </div>
              )}

              {/* Error messages */}
              {profileImageError && <div className="text-danger d-flex text-left">{profileImageError}</div>}
              {!profileImageError && formik.touched.profile_image && formik.errors.profile_image && <div className="text-danger d-flex text-left">{t(formik.errors.profile_image)}</div>}
            </div>

            {/* Brochure upload */}
            <div className="col-12 mt-3">
              <label className="form-label subtitle-1" htmlFor="upload_brochure">
                Upload Brochure
              </label>

              {!brochureImage ? (
                <div className="custom-file-upload">
                  <div className="custom-upload-btn">
                    <span>Browse</span>
                    <i className="fi fi-br-upload" />
                  </div>
                  <input id="upload_brochure" type="file" name="brochure" accept="image/jpeg, image/png, image/webp" onChange={handleBrochureChange} />
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded">
                  <span className="text-truncate me-2">
                    <i className="fi fi-rr-document me-1"></i>
                    {brochureImage}
                  </span>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={removeBrochureImage} title="Remove brochure">
                    <i className="fi fi-rr-cross-small" style={{ position: "relative", top: "2px" }}></i>
                  </button>
                </div>
              )}

              {/* Error messages */}
              {brochureError && <div className="text-danger d-flex text-left">{brochureError}</div>}
              {!brochureError && formik.touched.brochure && formik.errors.brochure && <div className="text-danger d-flex text-left">{t(formik.errors.brochure)}</div>}
            </div>

            <div className="col-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="agreeCheck"
                  name="isTermsConditionAccept"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={formik.values.isTermsConditionAccept}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.2rem" }}>
                  <label className="form-check-label mt-0.5" htmlFor="agreeCheck">
                    I agree to the <Link to="/business/terms-and-conditions">Terms &amp; Conditions</Link> and <Link to="/business/privacy-policy">Privacy Policy</Link>
                  </label>
                  {formik.touched.isTermsConditionAccept && formik.errors.isTermsConditionAccept ? <div className="text-danger text-left">{t(formik.errors.isTermsConditionAccept)}</div> : null}
                </div>
              </div>
            </div>
            <div className="col-md-12 text-center mt-3">
              <button type="submit" className="btn btn-light-purple  px-5">
                Continue
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessRegister;
